import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tooltip,
    Typography
} from '@material-ui/core';
import { GetApp, Reorder } from '@material-ui/icons';
import { saveAs } from 'file-saver';
import { kebabCase, orderBy, pick } from 'lodash';
import { unparse } from 'papaparse';
import React from 'react';

import { chartPalette } from 'shared/common/charts/constants';
import { buildDistributedHorizontalBarOption, buildDonutChartOption } from 'shared/common/charts/options';

import { preprocessData } from '../../common/chart-data-preprocessors';
import { echarts, ReactEChartsCore } from '../../common/charts/echarts-core';
import { usePortalTooltip } from '../../common/charts/tooltip';
import { useChartExport } from '../../hooks/use-chart-export';
import { ChartExportButtons } from '../charts/chart-export-buttons';

const legendLabelMaxLength = 48;

export function DataPanel<T extends { name: string; count: number }>({
    companyName,
    title,
    secondaryTitle,
    data,
    view,
    sort,
    onRawDownload
}: {
    companyName: string;
    title: string;
    secondaryTitle?: string;
    data: T[];
    view: 'table' | 'pie' | 'bar';
    sort: boolean;
    onRawDownload?: () => void;
}) {
    const chartExport = useChartExport({ title, scope: companyName });
    const renderEmptyChart = React.useCallback(
        (wrapperClass: string) => (
            <div
                className={wrapperClass}
                style={{ alignItems: 'center', display: 'flex', height: 480, justifyContent: 'center' }}
            >
                <Typography variant="body2" color="textSecondary">
                    No data available
                </Typography>
            </div>
        ),
        []
    );
    const initialOrdered = sort ? orderBy(data, ['count', 'name'], ['desc', 'asc']) : data;
    const ordered = orderBy(initialOrdered, [(val) => val.name?.toLowerCase() === 'unknown'], ['asc']);

    const handleDownloadCounts = () => {
        const fileName = kebabCase(`${companyName}-${title}`).toLowerCase() + '.csv';
        const lines = ordered.map((entity) => pick(entity, ['name', 'count']));
        const csvContent = unparse(lines);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, fileName);
    };

    const rows = ordered.map((entity, index) => (
        <TableRow key={index} hover={true} selected={false}>
            <TableCell>{entity.name}</TableCell>
            <TableCell className="count-col">{entity.count}</TableCell>
        </TableRow>
    ));

    const sum = ordered.reduce((acc, curr) => acc + curr.count, 0);

    const table = (
        <TableContainer>
            <Table>
                <TableBody>
                    {rows}
                    <TableRow className="totals-row">
                        <TableCell>Total</TableCell>
                        <TableCell className="count-col">{sum}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );

    const chartData = preprocessData(ordered);
    const categories = chartData.map((v) => v.name);
    const chartValues = chartData.map((v) => ({ name: v.name, value: v.count }));

    const pieTooltip = usePortalTooltip(
        {
            formatter: (raw: any) => {
                const params = Array.isArray(raw) ? raw[0] : raw;
                const percent = params?.percent ?? 0;
                const value = params?.value;
                return `
                    <div class="metrics-tooltip">
                        <div class="metrics-tooltip-header">${params?.name ?? ''}</div>
                        <div class="metrics-tooltip-value">${value} (${typeof percent === 'number' ? percent.toFixed(2) : 0}%)</div>
                    </div>
                `;
            },
            trigger: 'item'
        },
        [chartData]
    );

    const barTooltip = usePortalTooltip(
        {
            formatter: (raw: any) => {
                const params = Array.isArray(raw) ? raw[0] : raw;
                return `
                    <div class="metrics-tooltip">
                        <div class="metrics-tooltip-header">${params?.name ?? ''}</div>
                        <div class="metrics-tooltip-value">${params?.value ?? 0}</div>
                    </div>
                `;
            },
            trigger: 'item'
        },
        [chartData]
    );

    const pieChartOptions = React.useMemo(() => {
        if (!chartValues.length) {
            return undefined;
        }
        const valueByName = new Map(chartValues.map((entry) => [entry.name, entry.value]));
        return buildDonutChartOption({
            colors: chartPalette,
            data: chartValues,
            legend: {
                formatter: (name: string) => {
                    const value = valueByName.get(name) ?? 0;
                    const truncated =
                        name.length > legendLabelMaxLength ? `${name.slice(0, legendLabelMaxLength)}...` : name;
                    return `${truncated} (${value})`;
                }
            },
            tooltip: pieTooltip
        });
    }, [chartValues, pieTooltip]);

    const barChartOptions = React.useMemo(() => {
        if (!chartValues.length) {
            return undefined;
        }
        return buildDistributedHorizontalBarOption({
            categories,
            colors: chartPalette,
            data: chartValues,
            tooltip: barTooltip
        });
    }, [categories, chartValues, barTooltip]);

    let content;
    if (view === 'bar') {
        content = barChartOptions ? (
            <div className="bar-chart">
                <ReactEChartsCore
                    ref={chartExport.chartRef}
                    echarts={echarts}
                    option={barChartOptions}
                    notMerge={true}
                    lazyUpdate={true}
                    style={{ height: 480 }}
                    opts={{ renderer: 'svg' }}
                />
            </div>
        ) : (
            renderEmptyChart('bar-chart')
        );
    } else if (view === 'pie') {
        content = pieChartOptions ? (
            <div className="pie-chart">
                <ReactEChartsCore
                    ref={chartExport.chartRef}
                    echarts={echarts}
                    option={pieChartOptions}
                    notMerge={true}
                    lazyUpdate={true}
                    style={{ height: 480 }}
                    opts={{ renderer: 'svg' }}
                />
            </div>
        ) : (
            renderEmptyChart('pie-chart')
        );
    } else {
        content = table;
    }

    const showChartExport = view !== 'table' && Boolean(pieChartOptions || barChartOptions);
    const chartExportButtons = !showChartExport ? null : (
        <ChartExportButtons
            isExporting={chartExport.isExporting}
            onExportPng={chartExport.exportAsPng}
            onExportSvg={chartExport.exportAsSvg}
        />
    );

    const rawDownloadButton = onRawDownload ? (
        <Tooltip title="Download Raw Data">
            <IconButton size="small" onClick={onRawDownload}>
                <Reorder fontSize="small" />
            </IconButton>
        </Tooltip>
    ) : null;

    const secondaryTitleElement = secondaryTitle ? (
        <Typography variant="h4" component="h4">
            {secondaryTitle}
        </Typography>
    ) : null;

    return (
        <div className="table-section">
            <div className="table-section-header">
                <div>
                    <Typography variant="h4" component="h4">
                        {title}
                    </Typography>
                </div>
                <div className="buttons">
                    {secondaryTitleElement}
                    {chartExportButtons}
                    {rawDownloadButton}
                    <Tooltip title="Download CSV">
                        <IconButton size="small" onClick={handleDownloadCounts}>
                            <GetApp fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
            <div className={`content-wrapper ${view}`}>{content}</div>
        </div>
    );
}
